/** Revision-pinned scripture reads used by presentation components. */

import {
  findVersePartContainingAllOrdinals,
  formatBookName,
  formatVerseRange,
  getTranslationSuffix,
} from "./util/render";
import type { StandaloneVerseData } from "@rsc/scripture-model";
import {
  type ScriptureClientError,
  type ScriptureClientInterface,
  ScriptureHttpError,
} from "@rsc/scripture-client";
import { findLastIndex } from "./util/arrays";
import {
  ScriptureUtilInvalidVidRangeError,
  ScriptureUtilMissingTranslationError,
  ScriptureUtilReferenceRenderError,
} from "./util/errors";
import { err, errAsync, ok, okAsync, ResultAsync } from "neverthrow";
import { createPartialVerse } from "./verseParts";
import {
  isSingleVerse,
  makeRange,
  rangesOverlap,
  type VidRange,
  withTranslation,
} from "./ranges";

interface AddRemoveMetaInfo {
  firstVerse: StandaloneVerseData;
  lastVerse: StandaloneVerseData;
  vidsInBook: number[];
  firstVerseIndex: number;
  lastVerseIndex: number;
  numVerses: number;
}

/**
 * Fetch metadata and format a reference, pinning unversioned input before dependent reads.
 * Explicit revisions never fall back to latest. Missing boundaries or cross-book ranges
 * produce typed errors; a different local translation supplies only the local book name.
 * @param plain Render the translation suffix as text instead of HTML.
 * @param partLabel Explicit part label, true to infer it from selected words, or false to omit it.
 */
export function renderRangeReference(
  range: VidRange,
  client: ScriptureClientInterface,
  localTid: string,
  showTranslation: boolean,
  plain: boolean,
  partLabel: string | boolean = false,
): ResultAsync<
  string,
  | ScriptureClientError
  | ScriptureUtilReferenceRenderError
  | ScriptureUtilMissingTranslationError
> {
  const tid = range.tid || localTid;
  if (!range.tr) {
    return client
      .getTranslation(tid)
      .andThen((metadata) =>
        metadata.tr
          ? renderRangeReference(
              withTranslation(range, tid, metadata.tr),
              client,
              localTid,
              showTranslation,
              plain,
              partLabel,
            )
          : err(
              new ScriptureUtilReferenceRenderError(
                "The server did not supply a translation revision.",
              ),
            ),
      );
  }
  const translated = withTranslation(range, tid);
  const translationResult = client
    .getTranslation(tid, undefined, { tr: translated.tr })
    .mapErr((e) =>
      e instanceof ScriptureHttpError && e.response.status == 404
        ? new ScriptureUtilReferenceRenderError(`Translation ${tid} not found.`)
        : e,
    );
  const boundaryVersesResult = ResultAsync.combine([
    getRangeFirstVerse(translated, client),
    getRangeLastVerse(translated, client),
  ]).andThen(([firstVerse, lastVerse]) => {
    if (!firstVerse || !lastVerse) {
      return err(
        new ScriptureUtilReferenceRenderError(
          `Boundary verses ${translated.fromVid} and ${translated.toVid} not found in translation ${tid}.`,
        ),
      );
    }
    if (firstVerse.bid !== lastVerse.bid) {
      return err(
        new ScriptureUtilReferenceRenderError(
          `Boundary verses ${firstVerse.fromVid} and ${lastVerse.toVid} are not in the same book.`,
        ),
      );
    }
    return ok({ firstVerse, lastVerse });
  });
  const booksResult = boundaryVersesResult.andThen((boundaryVerses) => {
    const bid = boundaryVerses.firstVerse.bid;
    const bookResult = client
      .getBook(tid, bid, undefined, { tr: translated.tr })
      .mapErr((e) =>
        e instanceof ScriptureHttpError && e.response.status == 404
          ? new ScriptureUtilReferenceRenderError(
              `Book ${bid} not found in translation ${tid}.`,
            )
          : e,
      );
    const localBookResult =
      localTid == tid
        ? bookResult
        : client.getBook(localTid, bid).orElse((e) => {
            if (e instanceof ScriptureHttpError && e.response.status == 404) {
              return bookResult;
            } else {
              return err(e);
            }
          });
    return ResultAsync.combine([bookResult, localBookResult]).map(
      ([book, localBook]) => ({ book, localBook }),
    );
  });
  return ResultAsync.combine([
    translationResult,
    boundaryVersesResult,
    booksResult,
  ]).map(([translation, boundaryVerses, books]) => {
    const { firstVerse, lastVerse } = boundaryVerses;
    const { book, localBook } = books;
    const translationStr = getTranslationSuffix(
      showTranslation,
      translation.name,
      translation.abbreviation,
      plain,
    );
    let partLabelStr;
    if (!isSingleVerse(range)) {
      partLabelStr = "";
    } else if (typeof partLabel === "string") {
      partLabelStr = partLabel;
    } else if (
      partLabel &&
      translated.wordOrdinals.length &&
      firstVerse.parts.length
    ) {
      const part = findVersePartContainingAllOrdinals(
        firstVerse.parts.map(createPartialVerse),
        translated.wordOrdinals,
      );
      partLabelStr = part?.label ?? "";
    } else {
      partLabelStr = "";
    }
    return formatVerseRange(
      firstVerse.label,
      lastVerse.label,
      formatBookName(book, localBook),
      String(firstVerse.chapterNumber),
      String(lastVerse.chapterNumber),
      `${partLabelStr}${translationStr}`,
    );
  });
}

/**
 * Read the first overlapping verse using the range's revision, or latest when unpinned.
 * Return null when no verse overlaps, and a typed error when the translation is missing or the request fails.
 */
export function getRangeFirstVerse(
  range: VidRange,
  client: ScriptureClientInterface,
): ResultAsync<
  StandaloneVerseData | null,
  ScriptureClientError | ScriptureUtilMissingTranslationError
> {
  if (!range.tid) {
    return errAsync(
      new ScriptureUtilMissingTranslationError(
        "Can't get verses from a range without a translation ID",
      ),
    );
  }
  if (isSingleVerse(range)) {
    return getRangeVerses(range, client).map((verses) => verses[0] ?? null);
  }
  // For a range, sometimes the boundary VID does not exist in the translation, so we use the next best one inside
  // the required range.
  return client
    .getVersesStartingAtId(range.tid, range.fromVid, 1, undefined, {
      tr: range.tr,
    })
    .map((vv) => vv.find((verse) => rangesOverlap(range, verse)) ?? null);
}

/**
 * Read the last overlapping verse using the range's revision, or latest when unpinned.
 * Return null when no verse overlaps, and a typed error when the translation is missing or the request fails.
 */
export function getRangeLastVerse(
  range: VidRange,
  client: ScriptureClientInterface,
): ResultAsync<
  StandaloneVerseData | null,
  ScriptureClientError | ScriptureUtilMissingTranslationError
> {
  if (!range.tid) {
    return errAsync(
      new ScriptureUtilMissingTranslationError(
        "Can't get verses from a range without a translation ID",
      ),
    );
  }
  if (isSingleVerse(range)) {
    return getRangeFirstVerse(range, client);
  }
  // For a range, sometimes the boundary VID does not exist in the translation, so we use the next best one inside
  // the required range.
  return client
    .getVersesEndingAtId(range.tid, range.toVid, 1, undefined, { tr: range.tr })
    .map((vv) => vv.find((verse) => rangesOverlap(range, verse)) ?? null);
}

/**
 * Read verses for inclusive VID bounds using the explicit revision, or latest when unpinned.
 * A missing translation produces a typed error; an unavailable explicit revision is never replaced.
 */
export function getRangeVerses(
  range: VidRange,
  client: ScriptureClientInterface,
): ResultAsync<
  StandaloneVerseData[],
  ScriptureClientError | ScriptureUtilMissingTranslationError
> {
  if (!range.tid) {
    return errAsync(
      new ScriptureUtilMissingTranslationError(
        "Can't get verses from a range without a translation ID",
      ),
    );
  }
  return client.getVersesByIdRange(
    range.tid,
    range.fromVid,
    range.toVid,
    undefined,
    { tr: range.tr },
  );
}

/**
 * Resolve boundary verses and ordered book VIDs at one translation revision for range navigation.
 * Unpinned inputs first resolve latest metadata; missing boundaries, missing revision metadata,
 * and ranges crossing book boundaries produce typed errors.
 */
function getRangeNavigation(
  range: VidRange,
  client: ScriptureClientInterface,
): ResultAsync<
  AddRemoveMetaInfo,
  | ScriptureClientError
  | ScriptureUtilMissingTranslationError
  | ScriptureUtilInvalidVidRangeError
> {
  const tid = range.tid;
  if (!tid) {
    return errAsync(
      new ScriptureUtilMissingTranslationError(
        `Can't add or remove verses from a range without a translation: ${range.fromVid}-${range.toVid}`,
      ),
    );
  }
  if (!range.tr) {
    return client
      .getTranslation(tid)
      .andThen((metadata) =>
        metadata.tr
          ? getRangeNavigation(withTranslation(range, tid, metadata.tr), client)
          : err(
              new ScriptureUtilInvalidVidRangeError(
                "The server did not supply a translation revision.",
              ),
            ),
      );
  }
  const boundaryVersesResult = ResultAsync.combine([
    getRangeFirstVerse(range, client),
    getRangeLastVerse(range, client),
  ]).andThen(([firstVerse, lastVerse]) => {
    if (!firstVerse || !lastVerse) {
      return errAsync(
        new ScriptureUtilInvalidVidRangeError(
          `Failed to find bounding verses of range ${tid}/${range.fromVid}-${range.toVid}`,
        ),
      );
    }
    if (firstVerse.bid !== lastVerse.bid) {
      return errAsync(
        new ScriptureUtilInvalidVidRangeError(
          `Ranges across book boundaries are not supported: ${tid}/${range.fromVid}-${range.toVid}`,
        ),
      );
    }
    return okAsync({ firstVerse, lastVerse });
  });
  const vidsInBookResult = boundaryVersesResult.andThen(({ firstVerse }) =>
    client.getVerseIds(tid, firstVerse.bid, null, undefined, { tr: range.tr }),
  );
  return ResultAsync.combine([boundaryVersesResult, vidsInBookResult]).andThen(
    ([boundaryVerses, vidsInBook]) => {
      /**
       * Test inclusive membership while locating the range within the revision's ordered book VIDs.
       */
      const vidTouchesOurRange = (vid: number) =>
        boundaryVerses.firstVerse.fromVid <= vid &&
        boundaryVerses.lastVerse.fromVid >= vid;
      const firstVerseIndex = vidsInBook.findIndex(vidTouchesOurRange);
      const lastVerseIndex = findLastIndex(vidsInBook, vidTouchesOurRange);
      if (firstVerseIndex < 0 || lastVerseIndex < 0) {
        return err(
          new ScriptureUtilInvalidVidRangeError(
            `Failed to find bounding verses of range ${tid}/${range.fromVid}-${range.toVid}`,
          ),
        );
      }
      const numVerses = lastVerseIndex - firstVerseIndex + 1;
      return ok({
        firstVerse: boundaryVerses.firstVerse,
        lastVerse: boundaryVerses.lastVerse,
        vidsInBook,
        firstVerseIndex,
        lastVerseIndex,
        numVerses,
      });
    },
  );
}

/**
 * Trim up to n verses from the end without dropping below min verses.
 * Return a fresh range retaining selected words; trimmed ranges use the resolved revision,
 * including ranges already at or below min. Navigation failures remain typed errors.
 * @param n Nonnegative number of verses to remove; Infinity trims down to min.
 * @param min Positive minimum number of verses to retain.
 */
export function removeVersesAfter(
  range: VidRange,
  client: ScriptureClientInterface,
  n: number,
  min: number,
): ResultAsync<
  VidRange,
  | ScriptureClientError
  | ScriptureUtilMissingTranslationError
  | ScriptureUtilInvalidVidRangeError
> {
  return getRangeNavigation(range, client).andThen((metadata) => {
    if (metadata.numVerses <= min) {
      return ok(
        withTranslation(range, metadata.firstVerse.tid, metadata.firstVerse.tr),
      );
    }
    // Remove verses from the end without subceeding the minimum.
    const remove = Math.min(n, metadata.numVerses - min);
    const newLastVerseIndex = Math.max(metadata.lastVerseIndex - remove, 0);
    // We could do an extra back-end query to get the toVid of the last verse in the range, to make sure it includes
    // the entire compound verse. But range is not necessary because a range that touches a compound verse will
    // already render as if it includes the entire compound verse.
    const newLastVerseFromVid = metadata.vidsInBook[newLastVerseIndex];
    if (!newLastVerseFromVid) {
      return err(
        new ScriptureUtilInvalidVidRangeError(
          "Failed to find the last verse in the range.",
        ),
      );
    }
    return ok(
      makeRange(
        range.fromVid,
        newLastVerseFromVid,
        range.tid,
        range.wordOrdinals,
        metadata.firstVerse.tr,
      ),
    );
  });
}

/**
 * Trim up to n verses from the start without dropping below min verses.
 * Return a fresh range retaining selected words; trimmed ranges use the resolved revision,
 * including ranges already at or below min. Navigation failures remain typed errors.
 * @param n Nonnegative number of verses to remove.
 * @param min Positive minimum number of verses to retain.
 */
export function removeVersesBefore(
  range: VidRange,
  client: ScriptureClientInterface,
  n: number,
  min: number,
): ResultAsync<
  VidRange,
  | ScriptureClientError
  | ScriptureUtilMissingTranslationError
  | ScriptureUtilInvalidVidRangeError
> {
  return getRangeNavigation(range, client).andThen((metadata) => {
    if (metadata.numVerses <= min) {
      return ok(
        withTranslation(range, metadata.firstVerse.tid, metadata.firstVerse.tr),
      );
    }
    // Remove verses from the start without subceeding the minimum.
    const remove = Math.min(n, metadata.numVerses - min);
    const newFirstVerseIndex = Math.min(
      metadata.firstVerseIndex + remove,
      metadata.vidsInBook.length - 1,
    );
    const newFirstVerseFromVid = metadata.vidsInBook[newFirstVerseIndex];
    if (!newFirstVerseFromVid) {
      return err(
        new ScriptureUtilInvalidVidRangeError(
          "Failed to find the first verse in the range.",
        ),
      );
    }
    return ok(
      makeRange(
        newFirstVerseFromVid,
        range.toVid,
        range.tid,
        range.wordOrdinals,
        metadata.firstVerse.tr,
      ),
    );
  });
}

/**
 * Keep at most the first min verses using revision-aware navigation, without extending short ranges.
 * Preserve selected words and propagate typed navigation errors.
 * @param min Positive maximum number of verses to retain.
 */
export function limitRangeAfter(
  range: VidRange,
  client: ScriptureClientInterface,
  min: number,
): ResultAsync<
  VidRange,
  | ScriptureClientError
  | ScriptureUtilMissingTranslationError
  | ScriptureUtilInvalidVidRangeError
> {
  return removeVersesAfter(range, client, Number.POSITIVE_INFINITY, min);
}

/** Extend within the pinned book, shifting the other boundary to respect the display limit. */
function extendRange(
  range: VidRange,
  client: ScriptureClientInterface,
  n: number,
  max: number,
  before: boolean,
) {
  return getRangeNavigation(range, client).andThen((meta) => {
    const last = before
      ? meta.lastVerseIndex
      : Math.min(meta.lastVerseIndex + n, meta.vidsInBook.length - 1);
    const first = before
      ? Math.max(0, meta.firstVerseIndex - n)
      : meta.firstVerseIndex;
    const start = before ? first : Math.max(first, last - max + 1);
    const end = before ? Math.min(last, first + max - 1) : last;
    const fromVid = meta.vidsInBook[start];
    const endVid = meta.vidsInBook[end];
    if (fromVid === undefined || endVid === undefined)
      return errAsync(
        new ScriptureUtilInvalidVidRangeError("Invalid navigation bounds."),
      );
    return client
      .getVerseById(range.tid!, endVid, undefined, { tr: meta.firstVerse.tr })
      .map((verse) =>
        makeRange(fromVid, verse.toVid, range.tid, null, meta.firstVerse.tr),
      );
  });
}
export function addVersesBefore(
  range: VidRange,
  client: ScriptureClientInterface,
  n: number,
  max: number,
) {
  return extendRange(range, client, n, max, true);
}
export function addVersesAfter(
  range: VidRange,
  client: ScriptureClientInterface,
  n: number,
  max: number,
) {
  return extendRange(range, client, n, max, false);
}
